import { useMemo, useRef, useState } from 'react';
import {
  useClients,
  useClientStats,
  useClientTiers,
  useLatestClientPhotoUrls,
  createClient,
  normalizePhoneDigits,
} from '@busy/api';
import type { AcquisitionSource, ClientRow, Tier } from '@busy/api';
import { Avatar, ModalCloseButton, TierBadge, useToast } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import {
  clientSortKey,
  groupClientsByLetter,
} from '../../lib/clientGrouping';
import { AlphaJumpBar } from '../clients/AlphaJumpBar';
import { AcquisitionSourcePicker } from '../AcquisitionSourcePicker';

// Pre-cut check-in: pick (or create) the client who's in the chair, OR skip = walk-in.
// First step in the cut flow after Today's Chair → Walk-in. Sets pendingClient on the
// store and advances to the service picker. The picker then starts the cut.
export function PreCutCheckin() {
  const clients = useClients();
  const stats = useClientStats();
  const tiers = useClientTiers();
  const photoUrls = useLatestClientPhotoUrls();
  const pickClient = useCutTimer((s) => s.pickClient);
  const openChair = useCutTimer((s) => s.openChair);
  const closeModal = useCutTimer((s) => s.closeModal);
  // Walk-in vs appointment — captured HERE (instead of post-cut) so the
  // barber's decision lands while they're already paying attention to
  // who's in the chair. PostCutSummary still surfaces the picker as a
  // final correction step, seeded from this value.
  const pendingWasAppointment = useCutTimer((s) => s.pendingWasAppointment);
  const setPendingWasAppointment = useCutTimer((s) => s.setPendingWasAppointment);
  const toast = useToast();
  const t = useT();

  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  // Acquisition source captured at client creation — biggest pre-cut
  // data quality lever after walk-in/appointment. Caught at check-in
  // (vs the existing post-cut profile-edit path) so the answer lands
  // while the barber's still talking to the client about how they
  // heard about the shop. Null = unspecified (correctable later from
  // the client detail screen).
  const [newSource, setNewSource] = useState<AcquisitionSource | null>(null);
  const scrollRootRef = useRef<HTMLDivElement>(null);

  const tierByClient = useMemo(() => {
    const m = new Map<string, Tier>();
    (tiers.data ?? []).forEach((t) => m.set(t.client_id, t.tier));
    return m;
  }, [tiers.data]);

  const visitCountByClient = useMemo(() => {
    const m = new Map<string, number>();
    (stats.data ?? []).forEach((s) => m.set(s.client_id, s.visit_count));
    return m;
  }, [stats.data]);

  // Alphabetized scrollable list — matches the Clients route / Settings
  // panel UX. Search filters against the full roster; remaining rows are
  // sorted by last/first name and grouped under sticky A-Z headers inside
  // a bounded scroll area so the modal stays compact on phones.
  const filteredClients = useMemo(() => {
    const list = clients.data ?? [];
    const q = search.trim().toLowerCase();
    let arr = list;
    if (q) {
      const digits = q.replace(/\D/g, '');
      arr = list.filter((c) => {
        const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase();
        const phone = c.phone?.replace(/\D/g, '') ?? '';
        return name.includes(q) || (digits.length > 0 && phone.includes(digits));
      });
    }
    return [...arr].sort((a, b) => clientSortKey(a).localeCompare(clientSortKey(b)));
  }, [clients.data, search]);

  const sections = useMemo(
    () => groupClientsByLetter(filteredClients),
    [filteredClients],
  );

  // Live phone-collision check for the new-client form. The DB unique
  // index will refuse anyway (migration 0056) — this just prevents the
  // round-trip and offers a one-tap "Use existing" affordance that
  // checks the right client in for the cut.
  const newPhoneMatch = useMemo(() => {
    const digits = normalizePhoneDigits(newPhone);
    if (!digits) return null;
    return (clients.data ?? []).find(
      (c) => normalizePhoneDigits(c.phone) === digits,
    ) ?? null;
  }, [newPhone, clients.data]);

  function pickExisting(c: ClientRow) {
    pickClient({
      kind: 'existing',
      id: c.id,
      firstName: c.first_name,
      lastName: c.last_name,
      phone: c.phone,
    });
  }

  // No more "walk-in" picker option — every cut now needs a client. When the
  // barber doesn't have time to capture full info on a true walk-in, the
  // "Add new client" form lets every field be empty and falls back to a
  // generated "Anonymous Client####" handle (computed below from the
  // current user's existing roster so numbers don't collide on this device).
  function nextAnonymousLastName(): string {
    let max = 0;
    for (const c of clients.data ?? []) {
      if (c.first_name !== 'Anonymous') continue;
      const m = (c.last_name ?? '').match(/^Client(\d+)$/);
      if (m) {
        const n = parseInt(m[1] ?? '0', 10);
        if (Number.isFinite(n) && n > max) max = n;
      }
    }
    return `Client${String(max + 1).padStart(4, '0')}`;
  }

  async function createAndAdvance() {
    const trimmedFirst = newFirstName.trim();
    const trimmedLast = newLastName.trim();
    const trimmedPhone = newPhone.trim();
    // Both name fields blank → fully anonymous fallback. If just one is
    // blank, we honor what the barber typed (no auto-fill of just one half).
    const useAnonymous = !trimmedFirst && !trimmedLast;
    setCreating(true);
    // Anonymous fallback implies walk-in source unless the barber
    // explicitly picked something else — same principle as the
    // was_appointment auto-tag below.
    const effectiveSource: AcquisitionSource | null =
      newSource ?? (useAnonymous ? 'walkin' : null);
    const result = await createClient({
      first_name: useAnonymous ? 'Anonymous' : trimmedFirst,
      last_name: useAnonymous ? nextAnonymousLastName() : trimmedLast,
      phone: trimmedPhone || undefined,
      acquisition_source: effectiveSource,
    });
    setCreating(false);
    if (result.error || !result.data) {
      toast(t('cut.checkin.createError', { message: result.error?.message ?? 'unknown error' }), 'error');
      return;
    }
    const created = result.data;
    // Anonymous fallback (no name fields filled) is by definition a
    // walk-in — that's the only case the fallback exists for. If the
    // barber hasn't explicitly toggled the chip themselves, auto-fill
    // walk-in so the visit doesn't land with was_appointment=null. We
    // never overwrite an explicit choice — if the barber pre-tapped
    // Appointment for whatever reason, that survives.
    if (useAnonymous && pendingWasAppointment === null) {
      setPendingWasAppointment(false);
    }
    pickClient({
      kind: 'existing',
      id: created.id,
      firstName: created.first_name,
      lastName: created.last_name,
      phone: created.phone,
    });
  }

  return (
    // Modal-bound flex column. Header is shrink-0; the body is flex-1
    // min-h-0 so the inner alpha list fills whatever vertical space
    // remains.
    //
    // Sizing: when showing the LIST we force `h-[92vh]` so flex-1
    // propagates a definite height down to the list scroll wrapper —
    // otherwise the wrapper's absolute-positioned scroll content gives
    // it 0 intrinsic height and the entire list collapses to just the
    // bottom search bar. ModalShell already caps to 92vh, so this
    // matches that ceiling exactly.
    //
    // When showing the new-client FORM we drop back to max-h so the
    // modal sizes to the form's intrinsic height (no awkward empty
    // space below a 6-input form on a tall phone).
    <div className={`flex flex-col ${showNewForm ? 'max-h-[92dvh]' : 'h-[92dvh]'}`}>
      <div className="shrink-0 bg-navy-900 text-white px-6 pt-7 pb-6 rounded-t-3xl relative">
        <ModalCloseButton onClose={closeModal} variant="dark" />
        <button
          type="button"
          onClick={openChair}
          className="absolute top-4 left-4 text-white/70 hover:text-white inline-flex items-center gap-1 text-xs font-bold tracking-wide uppercase px-2 py-1 rounded hover:bg-white/10"
        >
          <ChevronLeft />
          {t('cut.back')}
        </button>
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 mt-2 mb-1.5">
          {t('cut.checkin.eyebrow')}
        </div>
        <h2 className="font-display text-3xl leading-tight">{t('cut.checkin.title')}</h2>
        <p className="text-white/70 text-sm mt-1.5">{t('cut.checkin.body')}</p>
      </div>

      <div className="flex-1 min-h-0 flex flex-col p-5 gap-3">
        {/* Walk-in vs appointment chips — visible from the start of
            check-in so the barber's pick travels through to the cut
            without an extra step at the end. Tap-to-toggle: tapping
            the active chip again clears the selection (null = unset).
            Two-chip layout matches the post-cut picker's existing
            chip styling for continuity. */}
        <div className="shrink-0">
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mb-1.5">
            {t('cut.checkin.sourceLabel')}
          </div>
          <div className="flex gap-2" role="radiogroup" aria-label={t('cut.checkin.sourceLabel')}>
            <SourceChip
              label={t('visitSource.walkin')}
              active={pendingWasAppointment === false}
              onClick={() =>
                setPendingWasAppointment(pendingWasAppointment === false ? null : false)
              }
            />
            <SourceChip
              label={t('visitSource.appointment')}
              active={pendingWasAppointment === true}
              onClick={() =>
                setPendingWasAppointment(pendingWasAppointment === true ? null : true)
              }
            />
          </div>
        </div>

        {showNewForm ? (
          <div className="rounded-xl border border-line bg-bg-alt p-4 space-y-3">
            <div className="font-display text-base">{t('cut.checkin.newClient')}</div>
            <input
              type="text"
              autoFocus
              placeholder={t('cut.checkin.firstName')}
              value={newFirstName}
              onChange={(e) => setNewFirstName(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
            />
            <input
              type="text"
              placeholder={t('cut.checkin.lastName')}
              value={newLastName}
              onChange={(e) => setNewLastName(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
            />
            <input
              type="tel"
              placeholder={t('cut.checkin.phone')}
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
            />
            {/* Acquisition source — single-line picker, optional. Captures
                the channel at the highest-attention moment (barber's
                just asked "how'd you hear about us?" while filling in
                the name). Goes straight onto the new client row. */}
            <div>
              <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mb-1.5">
                {t('cut.checkin.acquisitionLabel')}
              </div>
              <AcquisitionSourcePicker value={newSource} onChange={setNewSource} />
            </div>
            {newPhoneMatch && (
              // Phone collision banner — show the existing client and let
              // the barber pick them straight into the cut without making
              // a duplicate row. This is the cut-timer entry point's
              // version of the "open existing" affordance — instead of
              // routing to a detail page (no detail page in this flow)
              // we just check them in for the cut.
              <div className="rounded-lg border border-amber-500/60 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <strong className="block text-sm text-ink-900 dark:text-ink-100 truncate">
                    {t('newClient.dupPhone.title', {
                      name:
                        `${newPhoneMatch.first_name ?? ''} ${newPhoneMatch.last_name ?? ''}`.trim() ||
                        t('today.unnamed'),
                    })}
                  </strong>
                  <span className="text-xs text-ink-700 dark:text-ink-300">
                    {t('cut.checkin.dupPhone.body')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    pickExisting(newPhoneMatch);
                    setShowNewForm(false);
                  }}
                  className="shrink-0 h-9 px-3 rounded-lg bg-amber-600 text-white font-bold text-xs"
                >
                  {t('cut.checkin.dupPhone.useExisting')}
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={createAndAdvance}
                disabled={creating || !!newPhoneMatch}
                className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
              >
                {creating ? t('cut.checkin.adding') : t('cut.checkin.addContinue')}
              </button>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="px-4 h-11 rounded-lg border border-line bg-surface font-semibold"
              >
                {t('cut.checkin.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {filteredClients.length === 0 ? (
              <div className="shrink-0 flex flex-col items-center gap-3 px-1 py-3">
                <p className="text-sm text-ink-500 text-center">
                  {clients.data && clients.data.length > 0
                    ? t('cut.checkin.noMatches')
                    : t('cut.checkin.empty')}
                </p>
                <button
                  type="button"
                  onClick={() => setShowNewForm(true)}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold shadow-sm"
                >
                  <UserPlusIcon />
                  <span>{t('cut.checkin.addNewTitle')}</span>
                </button>
              </div>
            ) : (
              // Card frame with two stacked children: list area (alpha bar
              // lives here) + bottom search bar. Same pattern as the main
              // AlphaClientList; iOS-style bottom search.
              <div className="rounded-xl border border-line bg-surface overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="relative flex-1 min-h-0">
                  <div
                    ref={scrollRootRef}
                    className="absolute inset-0 overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                  <ul className="divide-y divide-line">
                    {sections.map(([letter, rows]) => (
                      <li key={letter}>
                        <h3
                          data-letter={letter}
                          className="sticky top-0 z-[1] px-3 py-1 bg-bg-alt text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 border-b border-line scroll-mt-2"
                        >
                          {letter}
                        </h3>
                        <ul className="divide-y divide-line">
                          {rows.map((c) => {
                            const name =
                              `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || t('today.unnamed');
                            const tier = tierByClient.get(c.id);
                            const visits = visitCountByClient.get(c.id) ?? 0;
                            const visitText =
                              visits === 0
                                ? t('cut.checkin.firstVisit')
                                : visits === 1
                                ? t('cut.checkin.visit_one')
                                : t('cut.checkin.visit_other', { count: visits });
                            return (
                              <li key={c.id}>
                                <button
                                  type="button"
                                  onClick={() => pickExisting(c)}
                                  className="w-full text-left bg-surface pl-3 pr-7 py-2.5 flex items-center gap-3 hover:bg-bg-alt"
                                >
                                  <Avatar
                                    name={name}
                                    size="md"
                                    tier={tier}
                                    photoUrl={photoUrls.data?.[c.id]}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <strong className="text-[15px] truncate">{name}</strong>
                                      {tier && (
                                        <TierBadge tier={tier} size="sm" label={t(`tier.${tier}`)} />
                                      )}
                                    </div>
                                    <div className="text-xs text-ink-500">
                                      {visitText}
                                      {c.phone ? ` · ${c.phone}` : ''}
                                    </div>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
                  <AlphaJumpBar
                    presentLetters={new Set(sections.map(([L]) => L))}
                    ariaLabel={t('clients.jumpAria')}
                    jumpToLabel={(L) => t('clients.jumpTo', { letter: L })}
                    scrollContainerRef={scrollRootRef}
                  />
                </div>
                <div className="shrink-0 border-t border-line bg-surface p-2 flex items-center gap-2">
                  <input
                    type="search"
                    placeholder={t('cut.checkin.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                    className="flex-1 h-10 px-4 rounded-lg border border-line bg-bg-alt text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewForm(true)}
                    aria-label={t('cut.checkin.addNewTitle')}
                    className="shrink-0 h-10 w-10 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 grid place-items-center font-bold shadow-sm active:scale-95 transition-transform"
                  >
                    <UserPlusIcon />
                  </button>
                </div>
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/** One of the two Walk-in / Appointment chips at the top of check-in.
 *  Tap toggles. Active state uses navy (light) / gold (dark) for the
 *  pill so it reads as a deliberate accent against the surrounding
 *  surface. Unselected state is a softer outline so neither option
 *  feels pre-defaulted. */
function SourceChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`flex-1 h-10 rounded-full text-[13px] font-bold transition-colors ${
        active
          ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 shadow-sm'
          : 'bg-bg-alt text-ink-700 border border-line hover:bg-bg-alt/70'
      }`}
    >
      {label}
    </button>
  );
}

function UserPlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}
